import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { rupiah, dateTimeFmt, numberFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Search, History, Phone } from "lucide-react";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [editId, setEditId] = useState(null);
  const [hist, setHist] = useState(null);
  const [histData, setHistData] = useState(null);

  const load = () => client.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const filtered = useMemo(() => items.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q)), [items, q]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama wajib diisi"); return; }
    try {
      if (editId) await client.put(`/customers/${editId}`, form);
      else await client.post("/customers", form);
      toast.success("Pelanggan disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openHist = async (c) => {
    setHist(c); setHistData(null);
    const r = await client.get(`/customers/${c.id}/sales`);
    setHistData(r.data);
  };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Daftar Pelanggan</h1>
          <p className="text-sm text-muted-foreground">Kelola pelanggan & lihat riwayat belanja per orang</p>
        </div>
        <Button onClick={() => { setForm({ name: "", phone: "", address: "" }); setEditId(null); setOpen(true); }} data-testid="add-customer-page-btn"><Plus className="mr-2 h-4 w-4" /> Tambah Pelanggan</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / nomor…" className="pl-9" data-testid="customer-search" />
      </div>

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>No. WhatsApp</TableHead><TableHead>Alamat</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>{c.phone ? <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-muted-foreground" />{c.phone}</span> : "-"}</TableCell>
                <TableCell className="text-muted-foreground">{c.address || "-"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openHist(c)} data-testid={`history-${c.id}`}><History className="mr-1 h-4 w-4" /> Riwayat</Button>
                    <Button size="icon" variant="ghost" onClick={() => { setForm({ name: c.name, phone: c.phone || "", address: c.address || "" }); setEditId(c.id); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Belum ada pelanggan</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Pelanggan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div className="space-y-2"><Label>No. WhatsApp</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xxxxxxxxxx" data-testid="cust-phone" /></div>
            <div className="space-y-2"><Label>Alamat</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="save-cust-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Riwayat Belanja — {hist?.name}</DialogTitle>
            <DialogDescription>{hist?.phone}</DialogDescription>
          </DialogHeader>
          {histData && (
            <div className="mb-3 flex gap-4">
              <div className="rounded-md bg-slate-100 px-4 py-2"><div className="text-xs text-muted-foreground">Transaksi</div><div className="font-mono font-semibold">{numberFmt(histData.count)}</div></div>
              <div className="rounded-md bg-blue-50 px-4 py-2"><div className="text-xs text-muted-foreground">Total Belanja</div><div className="font-mono font-semibold text-primary">{rupiah(histData.total_spent)}</div></div>
            </div>
          )}
          <Table>
            <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Waktu</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {histData?.sales?.map((s) => (
                <TableRow key={s.id}><TableCell className="font-mono text-xs">{s.number}</TableCell><TableCell className="text-xs">{dateTimeFmt(s.created_at)}</TableCell><TableCell className="text-right font-mono">{rupiah(s.total)}</TableCell></TableRow>
              ))}
              {histData && !histData.sales.length && <TableRow><TableCell colSpan={3} className="py-8 text-center text-muted-foreground">Belum ada belanja</TableCell></TableRow>}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
